import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getTableHeader, handleCellClick } from './rep-product-data-grouping';
import RepProductReportGraph from './rep-product-report-graph';
import RepProductReportDetail from './rep-product-report-detail';
import { Table, Spin } from 'antd';
import "./rep-product-report-total.css"
import { strDate2Server } from "../../../extensions/utils";
import { isMobile } from '../../../extensions/design';
import { useExcelData } from '../rep-excel-data-provider';

const chartColors = [
  '#fbc02d', '#1976d2', '#0288d1', '#388e3c', '#d32f2f', '#7b1fa2', '#ffa000',
  '#0288d1', '#388e3c', '#d32f2f', '#7b1fa2', '#ffa000'
];

function RepReportReportTotal({ filters, data, onRefresh, refreshTrigger, loading }) {
  const [chartType, setChartType] = useState('pie');
  const [detailData, setDetailData] = useState(null);
  const [expandedDetailGroups, setExpandedDetailGroups] = useState(new Set());
  const [selectedCell, setSelectedCell] = useState(null);
  const { setExcelDataTotal } = useExcelData();
  
  // Приводим входные данные к строкам таблицы и считаем "Всего"
  const tableRows = useMemo(() => {
    if (!Array.isArray(data)) return [];
  
    return data.map((item) => {
      const acc = {
        prodQuantity: 0, prodSum: 0, prodDiscount: 0, prodTotal: 0, prodSalary: 0,
        serviceQuantity: 0, serviceSum: 0, serviceDiscount: 0, serviceTotal: 0, serviceSalary: 0
        // subscriptionSum: 0, subscriptionDiscount: 0, subscriptionPaid: 0, subscriptionDebt: 0, subscriptionSalary: 0,
        // packetSum: 0, packetDiscount: 0, packetPaid: 0, packetDebt: 0, packetSalary: 0,
      };
  
      if (Array.isArray(item.items)) {
        for (const saleItem of item.items) {
          if (saleItem.saleType === 0) {
            acc.prodQuantity += saleItem.quantity ?? 0;
            acc.prodSum += saleItem.summa ?? 0;
            acc.prodDiscount += saleItem.discount ?? 0;
            acc.prodTotal += saleItem.total ?? 0;
            acc.prodSalary += saleItem.salary ?? 0;
          }
          if (saleItem.saleType === 1) {
            acc.serviceQuantity += saleItem.quantity ?? 0;
            acc.serviceSum += saleItem.summa ?? 0;
            acc.serviceDiscount += saleItem.discount ?? 0;
            acc.serviceTotal += saleItem.total ?? 0;
            acc.serviceSalary += saleItem.salary ?? 0;
          }
        }
      }
    
      return {
        key: item.userId?.toString() || item.employeeName,
        name: item.employeeName,
        userId: item.userId || null,
        ...acc,
        totalQuantity: acc.prodQuantity + acc.serviceQuantity,
        totalSum: acc.prodSum + acc.serviceSum,
        totalDiscount: acc.prodDiscount + acc.serviceDiscount,
        totalTotal: acc.prodTotal + acc.serviceTotal,
        totalSalary: acc.prodSalary + acc.serviceSalary,
      };
    });
  }, [data]);

const handleRowClick = useCallback((record) => {
  // Пропускаем клик по строке "Всего"
  if (record.key === 'product-summary-row') return;
  
  // Получаем userId из исходных данных
  const userData = data.find(item => 
    item.userId?.toString() === record.key || item.employeeName === record.name
  );
  
  if (userData && userData.userId) {
    // Открываем детализацию для сотрудника
    const detailDataResult = handleCellClick(data, record.name, [filters.startDate, filters.endDate], 'employee', 'Всего', 
      filters.reportType, tableRows, userData.userId);
    
    if (detailDataResult) {
      setDetailData(detailDataResult);
      setSelectedCell({ 
        rowName: record.name,
        section: 'employee', 
        metric: 'Всего',
        userId: userData.userId
      });
    }
  }
}, [data, filters.reportType, tableRows]);

const summaryRow = useMemo(() => {
  if (!Array.isArray(tableRows) || tableRows.length === 0) return null;

  const totals = tableRows.reduce((acc, row) => {
    acc.prodQuantity += row.prodQuantity;
    acc.prodSum += row.prodSum;
    acc.prodDiscount += row.prodDiscount;
    acc.prodTotal += acc.prodTotal;
    acc.prodSalary += row.prodSalary;

    acc.serviceQuantity += row.serviceQuantity;
    acc.serviceSum += row.serviceSum;
    acc.serviceDiscount += row.serviceDiscount;
    acc.serviceTotal += row.serviceTotal;
    acc.serviceSalary += row.serviceSalary;

    acc.totalSum += row.totalSum;
    acc.totalDiscount += row.totalDiscount;
    acc.totalQuantity += row.totalQuantity;
    acc.totalTotal += row.totalTotal;
    acc.totalSalary += row.totalSalary;
    return acc;
  }, {
    prodQuantity: 0, prodSum: 0, prodDiscount: 0, prodTotal: 0, prodSalary: 0,
    serviceQuantity: 0, serviceSum: 0, serviceDiscount: 0, serviceTotal: 0, serviceSalary: 0,
    totalQuantity: 0, totalSum: 0, totalDiscount: 0, totalTotal: 0, totalSalary: 0
  });

  return {
    key: 'product-summary-row',
    name: 'ИТОГО',
    userId: null,
    ...totals,
  };
}, [tableRows]);

  const fullDataSource = useMemo(() => [...tableRows, summaryRow], [tableRows, summaryRow]);

  const renderNumber = (value) => (value ? value.toLocaleString() : '');
  
  const isCellSelected = useCallback((rowName, section) => {
    if (!selectedCell) return false;

    return (
      selectedCell.rowName === rowName &&
      selectedCell.section === section
    );
  }, [selectedCell]);

const handleCellClickCallback = useCallback((rowName, section, reportType, userId = null) => {
  setSelectedCell({ rowName, section });

  setDetailData({
    employeeName: rowName,
    employeeId: userId,
    reportType: reportType
  });
}, []);

  const closeDetail = useCallback(() => {
    setDetailData(null);
    setExpandedDetailGroups(new Set());
    setSelectedCell(null);
  }, []);

  useEffect(() => {
    closeDetail();
  }, [refreshTrigger]);
  
  const showDebtColumns = filters.periodType !== 1;

  const columns = [
    {
      title: getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 140,
      render: (text) => <span>{text}</span>,
      onCell: () => ({ className: 'cell-center product-clickable-cell group-sep' }),
    },

    // Товары и услуги
    {
      title: <span className="product-header-cell">Кол.</span>,
      dataIndex: 'prodQuantity',
      key: 'prodQuantity',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Товары, услуги') ? 'product-selected-cell' : 'product-clickable-cell'),
          onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Товары, услуги', 0, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Сумма</span>,
      dataIndex: 'prodSum',
      key: 'prodSum',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Товары, услуги') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Товары, услуги', 0, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Скидка</span>,
      dataIndex: 'prodDiscount',
      key: 'prodDiscount',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Товары, услуги') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Товары, услуги', 0, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Итого</span>,
      dataIndex: 'prodTotal',
      key: 'prodTotal',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Товары, услуги', 'paid') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Товары, услуги', 0, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Зарплата</span>,
      dataIndex: 'prodSalary',
      key: 'prodSalary',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? 'group-sep'
          : (isCellSelected(row?.name, 'Товары, услуги') ? 'product-selected-cell group-sep' : 'product-clickable-cell group-sep'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Товары, услуги', 0, row.userId),
      }),
    },

    // Услуги из пакета
    {
      title: <span className="product-header-cell">Кол.</span>,
      dataIndex: 'serviceQuantity',
      key: 'serviceQuantity',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Услуги из пакета') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Услуги из пакета', 1, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Сумма</span>,
      dataIndex: 'serviceSum',
      key: '',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Услуги из пакета') ? 'product-selected-cell' : 'product-clickable-cell'),
          onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Услуги из пакета', 1, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Скидка</span>,
      dataIndex: 'serviceDiscount',
      key: 'serviceDiscount',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Услуги из пакета') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Услуги из пакета', 1, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Итого</span>,
      dataIndex: 'serviceTotal',
      key: 'serviceTotal',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Услуги из пакета') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Услуги из пакета', 1, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Зарплата</span>,
      dataIndex: 'serviceSalary',
      key: 'serviceSalary',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? 'group-sep'
          : (isCellSelected(row?.name, 'Услуги из пакета') ? 'product-selected-cell group-sep' : 'product-clickable-cell group-sep'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Услуги из пакета', 1, row.userId),
      }),
    },
    // Всего
    {
      title: <span className="product-header-cell">Кол.</span>,
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      align: 'right',
      width: 70,
      render: (value) => (
        <span>
          {renderNumber(value)}
        </span>
      ),
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Сумма</span>,
      dataIndex: 'totalSum',
      key: 'totalSum',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Скидка</span>,
      dataIndex: 'totalDiscount',
      key: 'totalDiscount',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Итого</span>,
      dataIndex: 'totalTotal',
      key: 'totalTotal',
      align: 'right',
      width: 70,
      render: renderNumber,
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? ''
          : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
      }),
    },
    {
      title: <span className="product-header-cell">Зарплата</span>,
      dataIndex: 'totalSalary',
      key: 'totalSalary',
      align: 'right',
      width: 70,
      render: (value, row, idx) => (
        <span>
          {row?.name !== 'ИТОГО' && (
          <span
            className="report-total-colorbox"
            style={{ background: chartColors[idx % chartColors.length] }}
          />
          )}
          {renderNumber(value)}
        </span>
      ),
      onCell: (row) => ({
        className: (row?.key === 'product-summary-row')
          ? 'group-sep'
          : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell group-sep' : 'product-clickable-cell group-sep'),
        onClick: (row?.key === 'product-summary-row')
          ? undefined
          : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
      }),
    },
  ];

  const prevExcelState = useRef(null);
  useEffect(() => {
    if (!columns?.length || !fullDataSource?.length) return;

    const newState = {
      title: 'Общий отчет',
      columnsCount: columns.length,
      rowsCount: fullDataSource.length,
    };

    // Проверяем, изменились ли данные реально
    if (
      prevExcelState.current &&
      prevExcelState.current.columnsCount === newState.columnsCount &&
      prevExcelState.current.rowsCount === newState.rowsCount
    ) {
      return; // ничего не делаем — данные те же
    }

    prevExcelState.current = newState;

    setExcelDataTotal({
      title: 'Общий отчет',
      columns,
      data: fullDataSource,
    });

  }, [columns, fullDataSource]);

  const customHeader = () => {
    return (
      <thead>
        <tr>
          <th
            rowSpan={2}
            className="product-header-cell header-cell-main group-sep"
            onClick={closeDetail}
          >
            {getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник'}
          </th>

          <th colSpan="5" className="product-header-cell group-sep">Товары, услуги</th>
          <th colSpan="5" className="product-header-cell group-sep">Услуги из пакета</th>
          <th colSpan="5" className="product-header-cell group-sep">Всего</th>
        </tr>
        <tr>
          {/* Абонементы */}
          <th className="product-header-cell">Кол.</th>
          <th className="product-header-cell">Сумма</th>
          <th className="product-header-cell">Скидка</th>
          <th className="product-header-cell">Итого</th>
          <th className="product-header-cell group-sep">Зарплата</th>
          {/* Пакеты */}
          <th className="product-header-cell">Кол.</th>
          <th className="product-header-cell">Сумма</th>
          <th className="product-header-cell">Скидка</th>
          <th className="product-header-cell">Итого</th>
          <th className="product-header-cell group-sep">Зарплата</th>
          {/* Всего */}
          <th className="product-header-cell">Кол.</th>
          <th className="product-header-cell">Сумма</th>
          <th className="product-header-cell">Скидка</th>
          <th className="product-header-cell">Итого</th>
          <th className="product-header-cell group-sep">Зарплата</th>
        </tr>
      </thead>
    );
  };

  const mobileHeader = () => (
    <thead>
      <tr>
        <th
          rowSpan={1}
          className="product-header-cell header-cell-main"
          onClick={closeDetail}
        >
          {getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник'}
        </th>
        <th className="product-header-cell">Кол.</th>
        <th className="product-header-cell">Сумма</th>
        <th className="product-header-cell">Скидка</th>
        <th className="product-header-cell">Итого</th>
        <th className="product-header-cell">Зарплата</th>
      </tr>
    </thead>
  );

  if (isMobile)
    return (
      <div className="abon-total report-total-flex">
        {loading ? (
        <div className="report-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
        <div className="date-period">
          Период {filters.startDate?.format('DD.MM.YY')} - {filters.endDate?.format('DD.MM.YY')}
        </div>
        <div className="report-total-main">
          <div>
            <Table
              columns={[
                {
                  title: getTableHeader ? getTableHeader(filters.reportType) : 'Сотрудник',
                  dataIndex: 'name',
                  key: 'name',
                  fixed: 'left',
                  width: 100,
                  onHeaderCell: () => ({ onClick: closeDetail, className: 'header-cell header-cell-main' }),
                },
                {
                  title: 'Кол.',
                  dataIndex: 'totalQuantity',
                  key: 'totalQuantity',
                  align: 'right',
                  width: 50,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row?.key === 'product-summary-row') ? '' : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
                    onClick: (row?.key === 'product-summary-row') ? undefined : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
                  }),
                },
                {
                  title: 'Сумма',
                  dataIndex: 'totalSum',
                  key: 'totalSum',
                  align: 'right',
                  width: 45,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row?.key === 'product-summary-row') ? '' : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
                    onClick: (row?.key === 'product-summary-row') ? undefined : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
                  }),
                },
                {
                  title: 'Скидка',
                  dataIndex: 'totalDiscount',
                  key: 'totalDiscount',
                  align: 'right',
                  width: 45,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row?.key === 'product-summary-row') ? '' : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
                    onClick: (row?.key === 'product-summary-row') ? undefined : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
                  }),
                },
                {
                  title: 'Итого',
                  dataIndex: 'totalTotal',
                  key: 'totalTotal',
                  align: 'right',
                  width: 50,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row?.key === 'product-summary-row') ? '' : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
                    onClick: (row?.key === 'product-summary-row') ? undefined : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
                  }),
                },
                {
                  title: 'Зарплата',
                  dataIndex: 'totalSalary',
                  key: 'totalSalary',
                  align: 'right',
                  width: 50,
                  render: renderNumber,
                  onCell: (row) => ({
                    className: (row?.key === 'product-summary-row') ? '' : (isCellSelected(row?.name, 'Всего') ? 'product-selected-cell' : 'product-clickable-cell'),
                    onClick: (row?.key === 'product-summary-row') ? undefined : () => handleCellClickCallback(row?.name, 'Всего', null, row.userId),
                  }),
                },
              ]}
              dataSource={fullDataSource}
              pagination={false}
              bordered
              size="small"
              rowClassName={(row) => row?.key === 'product-summary-row' ? 'product-total-row' : ''}
              components={{ header: { wrapper: mobileHeader } }}
              className="report-total-table-normal"
            />
          </div>
          {detailData && (
            <div className="report-total-detail-wrapper">
              <RepProductReportDetail
                detailData={detailData.items}
                onClose={closeDetail}
                filterParams={{
                  startDate: strDate2Server(filters.startDate),
                  endDate: strDate2Server(filters.endDate),
                  productIds: filters.productIds,
                  priceFilterType: filters.priceFilterType,
                  employeeType: filters.employeeType,
                }}
                detailParams={{
                  employeeId: detailData.employeeId,
                  selectedRowName: detailData.employeeName,
                  ...(detailData.reportType != null && { reportType: detailData.reportType })
                }}
                expandedGroups={expandedDetailGroups}
                setExpandedGroups={setExpandedDetailGroups}
            />
            </div>
          )}
          {!detailData && (
              <RepProductReportGraph
              data={tableRows.map((row) => ({ name: row.name, total: row.totalSalary }))}
              chartType={chartType}
              setChartType={setChartType}
              reportType={filters.reportType}
            />
          )}
        </div>
      </>
      )}
      </div>
    );

  return (
    <div className="abon-total report-total-flex">
      {loading ? (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    ) : (
      <>
      <RepProductReportGraph
        data={tableRows.map((row) => ({ name: row.name, total: row.totalSalary }))}
        chartType={chartType}
        setChartType={setChartType}
        reportType={filters.reportType}
      />
      <div className="report-total-main">
        <div className="print-title">
          Отчёт по продажам товаров, услуг за период {filters.startDate?.format("DD.MM.YYYY")} — {filters.endDate?.format("DD.MM.YYYY")}
        </div>
        <div>
          <Table
            columns={columns}
            dataSource={fullDataSource}
            pagination={false}
            bordered
            size="small"
            rowClassName={(row) => row?.key === 'product-summary-row' ? 'product-total-row' : ''}
            components={{ header: { wrapper: customHeader } }}
            className="report-total-table-normal"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
          />
        </div>
        {detailData && (
          <div className="report-total-detail-wrapper">
            <RepProductReportDetail
              detailData={detailData.items}
              onClose={closeDetail}
              filterParams={{
                startDate: strDate2Server(filters.startDate),
                endDate: strDate2Server(filters.endDate),
                productIds: filters.productIds,
                priceFilterType: filters.priceFilterType,
                employeeType: filters.employeeType,
              }}
              detailParams={{
                employeeId: detailData.employeeId ? detailData.employeeId : 0,
                selectedRowName: detailData.employeeName,
                ...(detailData.reportType != null && { reportType: detailData.reportType })
              }}
              expandedGroups={expandedDetailGroups}
              setExpandedGroups={setExpandedDetailGroups}
            />
          </div>
        )}
      </div>
      </>
    )}
    </div>
  );
}

export default RepReportReportTotal;
